import { useEffect } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { SearchPrompt } from 'src/api/v1-api';
import { CategorySelector } from 'src/components/molecules';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';

interface SearchPromptFormProps {
  searchPrompt: Partial<SearchPrompt> | null;
  onCancel: () => void;
  onUpdate: (data: SearchPrompt) => void;
  onCreate: (data: Partial<SearchPrompt>) => void;
}

interface SearchPromptFields {
  prompt: string;
  category_id?: number;
}

const SearchPromptForm = ({
  searchPrompt,
  onCancel,
  onUpdate,
  onCreate,
}: SearchPromptFormProps) => {
  const { marketplace } = useMarketplaceContext();
  const showToast = useSingleToast();

  const defaultValues: SearchPromptFields = {
    prompt: searchPrompt?.prompt || '',
    category_id: searchPrompt?.category?.id,
  };

  const { control, handleSubmit, reset, setValue, watch } = useForm<SearchPromptFields>({
    defaultValues,
  });

  const categoryId = watch('category_id');

  useEffect(() => {
    if (searchPrompt) {
      reset({
        prompt: searchPrompt.prompt || '',
        category_id: searchPrompt.category?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPrompt]);

  const buildPayload = (
    data: SearchPromptFields,
    run: boolean,
  ): Partial<SearchPrompt & { category_id: number; run: boolean }> => {
    return {
      ...(searchPrompt || {}),
      prompt: data.prompt,
      category_id: data.category_id,
      run,
    };
  };

  const decorateBeforeSubmitData = (run: boolean) => (data: SearchPromptFields) => {
    if (!data.prompt || !data.category_id) {
      showToast({
        status: 'error',
        title: 'Missing Required Fields',
        description: 'Please ensure all required fields are filled out.',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const payload = buildPayload(data, run);

    if (searchPrompt?.id) {
      onUpdate(payload as SearchPrompt);
    } else {
      onCreate(payload);
    }
  };

  return (
    <Flex direction="column" w="100%">
      <Flex direction="row" alignItems="center" justifyContent="space-between">
        <Text fontWeight="semibold" pb={4}>
          {searchPrompt?.id ? 'Edit' : 'New'} Search Prompt
        </Text>
      </Flex>
      <Stack as="form" onSubmit={handleSubmit(decorateBeforeSubmitData(false))}>
        <Flex width={'100%'} gap={4} direction="column">
          <Stack width="100%">
            <Controller
              name="prompt"
              control={control}
              render={({ field }) => (
                <FormControl id="prompt" isRequired>
                  <FormLabel>Prompt</FormLabel>
                  <Textarea placeholder="Enter your prompt..." {...field} />
                </FormControl>
              )}
            />
          </Stack>
          <Stack width="fit-content">
            <FormControl id="category_id" isRequired>
              <FormLabel>Category</FormLabel>
              <CategorySelector
                mode="category-admin"
                marketplaceId={marketplace?.id}
                onCategorySelect={(category) => setValue('category_id', category?.id)}
                selectedCategoryId={categoryId ? categoryId.toString() : undefined}
              />
            </FormControl>
          </Stack>
          <Flex direction="row" alignItems="center" my={4} w="100%">
            <Flex
              direction="row"
              alignItems="center"
              gap={4}
              justifyContent="flex-end"
              wrap={'wrap'}
              flexGrow="1"
            >
              <Button type="button" onClick={onCancel}>
                Cancel
              </Button>
              <Button colorScheme="primary" type="submit">
                Save
              </Button>
              <Button colorScheme="primary" onClick={handleSubmit(decorateBeforeSubmitData(true))}>
                Save & Run
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Stack>
    </Flex>
  );
};

export default SearchPromptForm;
